// Publishing composable — manages publishing state and progress for entries
import { ref, computed } from 'vue'
import type { PublishProgress } from '@/types'
import { useToasts } from './useToasts'
import { useI18n } from './useI18n'

const publishingEntryId = ref<string | null>(null)
const progress = ref<PublishProgress | null>(null)
const pollInterval = ref<number | null>(null)

export function usePublishing() {
  const { showToast } = useToasts()
  const { t } = useI18n()

  const isPublishing = computed(() => publishingEntryId.value !== null)

  function isEntryPublishing(entryId: string): boolean {
    return publishingEntryId.value === entryId
  }

  function getProgress(entryId: string): PublishProgress | null {
    if (publishingEntryId.value !== entryId) return null
    return progress.value
  }

  function getProgressPercent(entryId: string): number {
    const p = getProgress(entryId)
    if (!p || p.total_targets === 0) return 0
    return Math.round((p.sent_count / p.total_targets) * 100)
  }

  async function startPublishing(entryId: string, onComplete?: () => void) {
    // If already publishing something, don't start another
    if (publishingEntryId.value) {
      showToast('Already publishing another entry.')
      return false
    }

    publishingEntryId.value = entryId
    progress.value = { total_targets: 0, sent_count: 0, failed_count: 0, is_complete: false }

    try {
      const res = await fetch(`/api/admin/events/${entryId}/publish`, { method: 'POST' })
      if (!res.ok) {
        showToast('Publish failed.')
        stopPublishing()
        return false
      }
      // Start polling progress
      pollProgress(entryId, onComplete)
      return true
    } catch {
      showToast('Publish failed.')
      stopPublishing()
      return false
    }
  }

  function pollProgress(entryId: string, onComplete?: () => void) {
    if (pollInterval.value) {
      clearInterval(pollInterval.value)
    }

    pollInterval.value = window.setInterval(async () => {
      // Safety check: if entry changed, stop polling
      if (publishingEntryId.value !== entryId) {
        stopPolling()
        return
      }

      try {
        const res = await fetch(`/api/admin/events/${entryId}/progress`)
        if (res.ok) {
          const data: PublishProgress = await res.json()
          progress.value = data
          if (data.is_complete) {
            stopPolling()
            showToast(t('publish-success'), 'success')
            publishingEntryId.value = null
            progress.value = null
            if (onComplete) {
              setTimeout(onComplete, 1500)
            }
          }
        }
      } catch {
        stopPolling()
        showToast('Progress check failed.')
        publishingEntryId.value = null
        progress.value = null
      }
    }, 1000)
  }

  function stopPolling() {
    if (pollInterval.value) {
      clearInterval(pollInterval.value)
      pollInterval.value = null
    }
  }

  function stopPublishing() {
    stopPolling()
    publishingEntryId.value = null
    progress.value = null
  }

  return {
    isPublishing,
    isEntryPublishing,
    getProgress,
    getProgressPercent,
    startPublishing,
    stopPublishing,
  }
}
